import React, { useRef } from 'react'
import { List, ExpandableListItem, Separator, Divider, Skeleton, Row } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import {
  getMostRecentFieldValue,
  getNestedSubelementsByType,
  useSlsStateContext,
  useSlsDispatchContext,
} from '@fs/zion-sls'
import PersonDisplay from './PersonDisplay'
import DraggablePerson from './dragNames/DraggablePerson'

function getPersonGivenName(person) {
  const foundGiven = getNestedSubelementsByType(person, 'NAME_GN')
  const givenName = getMostRecentFieldValue(foundGiven?.[0]) || ''
  return givenName
}

function PersonStyle({ person, movePersonMode, record, t }) {
  return (
    <>
      {movePersonMode && <DraggablePerson person={person} record={record} t={t} />}
      {!movePersonMode && (
        <PersonDisplay
          record={record}
          person={person}
          openNewPage={() => {
            window.open(`/ark:/61903/${person.partitionKey}?view=index&personArk=/ark:/61903/${person.id}&action=view`)
          }}
          t={t}
          key={person.id}
        />
      )}
    </>
  )
}

function ExpandableListContent({ children, givenName, initiallyOpen = false }) {
  const [t] = useTranslation()

  return (
    <List>
      <ExpandableListItem
        initiallyOpen={initiallyOpen}
        primaryText={t('other-persons-mentioned.mentioned.heading', 'Others mentioned in this record')}
        secondaryText={t(
          'other-persons-mentioned.these-are-referenced.sub-heading',
          'These are referenced in relation to {givenName} on other pages',
          { givenName }
        )}
      >
        {children}
      </ExpandableListItem>
    </List>
  )
}

export default function OtherPersonsMentioned({ record, movePersonMode }) {
  const [t] = useTranslation()
  const { currentImageArk } = useSlsStateContext()
  const zionSlsDispatch = useSlsDispatchContext()
  const initialOpen = useRef(false)

  const allPersons = getNestedSubelementsByType(record, 'PERSON')
  const primaryPerson = allPersons?.find((p) => p.primary === true)

  const danglingPersons = allPersons.filter((person) => person?.partitionKey !== currentImageArk) || []

  // If I'm not on the primary person's image, use the first person to appear
  let referencePerson = primaryPerson
  if (primaryPerson?.partitionKey !== currentImageArk) {
    referencePerson = allPersons.find((person) => person?.partitionKey === currentImageArk)
  }

  // Set up variables for the skeleton
  // If the record is not the current image, we need to show a skeleton while loading the images
  const danglingArksFromRecord = (record?.subElements ?? [])
    .filter((el) => el.isDangling && el.partitionKey !== currentImageArk)
    .map((el) => el.partitionKey)

  const showSkeleton = referencePerson && danglingArksFromRecord.length > 0 && danglingPersons.length === 0
  // check the amount of skeletons to show
  const amountSkeletonsRaw = (record?.subElements?.length || 0) - allPersons.length
  // limit the amount of skeletons to show
  const amountSkeletons = Math.min(Math.max(amountSkeletonsRaw, 3), 6)

  const loadArkImages = () => {
    initialOpen.current = true

    zionSlsDispatch({
      type: 'ADD_ARKS_TO_LOAD',
      value: danglingArksFromRecord,
    })
  }

  return (
    <>
      {showSkeleton && (
        <Row onClick={loadArkImages} data-testid="load-ark-images-row">
          <ExpandableListContent givenName={getPersonGivenName(referencePerson)}>
            {Array.from({ length: amountSkeletons }).map((_, idx) => (
              <Skeleton.ListItem key={`skeleton-${idx + 1}`} hasSecondLine />
            ))}
          </ExpandableListContent>
        </Row>
      )}
      {danglingPersons.length > 0 && referencePerson && (
        <ExpandableListContent givenName={getPersonGivenName(referencePerson)} initiallyOpen={initialOpen.current}>
          {danglingPersons.map((person) => (
            <PersonStyle key={person.id} person={person} movePersonMode={movePersonMode} record={record} t={t} />
          ))}
        </ExpandableListContent>
      )}
      {danglingPersons.length > 0 && !referencePerson && (
        <>
          <Separator size="xs" />
          <Divider fullWidth />
          <Separator size="xs" />
          {danglingPersons.map((person) => (
            <PersonStyle key={person.id} person={person} movePersonMode={movePersonMode} record={record} t={t} />
          ))}
        </>
      )}
    </>
  )
}
